import { Component, computed, effect, EffectRef, ElementRef, inject, Signal, ViewChild } from '@angular/core';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { ButtonType } from '../../../../../models/enums/components/Button/ButtonType.enum';
import { AppViews } from '../../../../../models/enums/dvm/DvmViews.enum';
import { DvmService } from '../../../../../services/dvm.service';
import { SearchService } from '../../../../../services/components/search.service';
import { NavigationService } from '@services/navigation.service';
import { NavigationItem } from '@models/types/app/NavigationItem.type';
import { ActivatedRoute } from '@angular/router';
import { AmenitiesService } from '@services/amenities.service';
import { FilterService } from '@services/filter.service';
import anime from 'animejs';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';

@Component({
  selector: 'app-back-button',
  standalone: true,
  imports: [ButtonComponent],
  templateUrl: './back-button.component.html',
  styleUrl: './back-button.component.css'
})
export class BackButtonComponent {

  @ViewChild('backButton', { read: ElementRef })
  private backButton!: ElementRef<ButtonComponent>;

  // SERVICES
  private   readonly route:             ActivatedRoute           = inject(ActivatedRoute);
  private   readonly dvmService:        DvmService               = inject(DvmService)
  private   readonly navigation:        NavigationService        = inject(NavigationService);
  private   readonly amsService:        AmenitiesService         = inject(AmenitiesService);
  private   readonly filterService:     FilterService            = inject(FilterService);
  private   readonly SearchService: SearchService            = inject(SearchService);



  // GLOBAL STATE
  protected readonly history:           Signal<NavigationItem[]> = this.navigation.history;
  protected readonly view:              Signal<AppViews | null>  = this.dvmService.view;
  protected readonly hasViewId:         boolean                  = this.route.snapshot.queryParamMap.has('view_id');


  // STATE
  protected          showElement:       boolean                  = false;
  protected          animationDuration: number                   = inject(SearchService).duration;
  protected readonly showBackButton:    Signal<boolean>          = computed((): boolean => ( (this.view() === AppViews.Pano && this.history().length > 0) || this.history().length > 1) && !this.hasViewId );

  //CONFIG
  private readonly uiconfig:            Signal<UiConfiguration>  = inject(UiService).uiConfig;
  private readonly gaExceptions:        Signal<GeneralAdmission> = computed((): GeneralAdmission=> this.uiconfig().GeneralAdmission);
    
  
  // BUTTON 
  protected readonly buttonType:        ButtonType               = ButtonType.Pill;


  // EFFECTS
  public backButtonVisibility:          EffectRef                = effect((): void => {
    
    const show: boolean = this.showBackButton();

    if( this.showElement === show ) return
      
    this.toggleBackButton(show);

  })


  // METHODS
  public back(): Promise<void> | void {
    
    if (this.view() !== AppViews.Pano) {      
      return this.handleBack();
    }
    
    const isGaException = this.gaExceptions().enabled && this.gaExceptions().data.includes(this.history()[1]?.map_id);
    const isInterior = /\(INT\)(R|E)?/.test(this.history()[1]?.map_id);
    
    if (isGaException || isInterior) {
      return this.navigation.back().then(() => {
        this.dvmService.close3dView();
      });
    } else if(this.history().length === 1) {    
      const topviewMapId = this.navigation.history()[0].map_id;  
      this.dvmService.loadMap(topviewMapId).subscribe();
      return this.dvmService.close3dView();
    } else {
      return this.dvmService.close3dView();
    }
  }

  private handleBack(): void {
    if(this.SearchService.searchValue()) {
      this.SearchService.setSearchValue(null);
    }

    if(this.amsService.hasSelectedAmenities() && !this.filterService.hasFilters) {
      this.amsService.resetSelectedAmenities();
    }

    this.navigation.back().then(previousItem => previousItem ? 
      this.dvmService.loadMap(previousItem.map_id).subscribe() :
      null
    )

  }
  
  private toggleBackButton(show: boolean): void {
    
    anime({
      targets:  this.backButton.nativeElement,
      opacity:  show ? 1 : 0,
      duration: this.animationDuration,
      easing:   'easeInOutQuad',
      complete: () => this.showElement = show,
    })

  }

}
